import { checkBoundLicense, licenseHolderName, licenseStateOf, type BoundLicense, type LicenseState } from '../../supabase/functions/_shared/accountProtocol';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_ACCOUNT_CACHE_KEY } from '../persistence/persistenceContract';
import { createStore } from './createStore';

// The account's bound New York licence as the server last confirmed it (Owner decision list 2026-10-19
// item 50, accountProtocol.ts L-1 … L-6), cached per account. It is the ONLY source of the licence and of
// the report's inspector name on this device (src/state/preferences.ts). Kept free of network code so the
// profile can read it without an import cycle; src/state/accountStore.ts writes it.
interface CachedAccount {
  license: unknown;
  syncedAt: number | null;
}

export const boundLicenseStore = createStore<{ license: BoundLicense | null; loaded: boolean }>({ license: null, loaded: false });

function readCache(): BoundLicense | null {
  try {
    const key = scopedStorageKey(V2_ACCOUNT_CACHE_KEY);
    if (key === null) return null;
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as CachedAccount | null;
    return parsed && parsed.license ? checkBoundLicense(parsed.license) : null;
  } catch {
    return null;
  }
}

export function cachedBoundLicense(): BoundLicense | null {
  const state = boundLicenseStore.get();
  if (state.loaded) return state.license;
  const license = readCache();
  boundLicenseStore.set(() => ({ license, loaded: true }));
  return license;
}

export function writeBoundLicense(license: BoundLicense | null, syncedAt: number | null): void {
  try {
    const key = scopedStorageKey(V2_ACCOUNT_CACHE_KEY);
    if (key !== null) window.localStorage.setItem(key, JSON.stringify({ license, syncedAt } satisfies CachedAccount));
  } catch {
    // storage refusal must never block field work
  }
  boundLicenseStore.set(() => ({ license, loaded: true }));
}

export function boundLicenseState(at: number = Date.now()): LicenseState {
  return licenseStateOf(cachedBoundLicense(), at);
}

export function boundHolderName(): string | null {
  const license = cachedBoundLicense();
  return license ? licenseHolderName(license) : null;
}

// Entering / leaving an account: the next read comes from that account's own cache.
export function forgetBoundLicenseMemory(): void {
  boundLicenseStore.set(() => ({ license: null, loaded: false }));
}

// Test seam: a licence the server confirmed, written exactly as a successful binding writes it.
export function setBoundLicenseForTests(license: BoundLicense | null): void {
  writeBoundLicense(license, license ? Date.now() : null);
}
